package com.speedsoftware.nibbler.ui.auth

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.speedsoftware.nibbler.client.NibblerApiClient
import com.speedsoftware.nibbler.models.AuthRequest
import com.speedsoftware.nibbler.models.AuthResponse
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.Screen
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

enum class LoginStep { CREDENTIALS, TWO_FACTOR }

/**
 * State and actions of the login, shared by the Compose form and the HTML form used on the web, so both
 * behave identically: password first, then the authenticator (or recovery) code if the account has 2FA.
 */
class LoginFlow(
    private val appState: AppState,
    private val scope: CoroutineScope
) {
    var step by mutableStateOf(LoginStep.CREDENTIALS)
        private set
    var isLoading by mutableStateOf(false)
        private set
    var errorMessage by mutableStateOf<String?>(null)
    var serverUrl by mutableStateOf(appState.serverBaseUrl)

    /** Called right before navigating away after a successful login (the web form hands credentials to the browser here). */
    var onLoggedIn: (() -> Unit)? = null

    private var challengeToken: String? = null

    init {
        appState.loginNotice?.let { errorMessage = it; appState.loginNotice = null }
    }

    fun updateServerUrl(url: String) {
        serverUrl = url
        appState.serverBaseUrl = url
        appState.apiClient.baseUrl = url
        appState.apiClient.cookieSessions = com.speedsoftware.nibbler.isSameOriginServer(url)
    }

    fun submitCredentials(email: String, password: String, server: String = serverUrl) {
        if (isLoading) return
        if (email.isBlank() || password.isBlank()) {
            errorMessage = "Email and password cannot be empty"
            return
        }
        updateServerUrl(server)
        isLoading = true
        errorMessage = null
        scope.launch {
            val result = appState.apiClient.login(AuthRequest(email.trim(), password))
            isLoading = false
            result.onSuccess { outcome ->
                when (outcome) {
                    is NibblerApiClient.LoginOutcome.Success -> finish(outcome.auth)
                    is NibblerApiClient.LoginOutcome.TwoFactorRequired -> {
                        challengeToken = outcome.challengeToken
                        step = LoginStep.TWO_FACTOR
                    }
                }
            }.onFailure { errorMessage = describe(it) }
        }
    }

    fun submitCode(code: String) {
        if (isLoading) return
        val token = challengeToken ?: run { backToCredentials(); return }
        if (code.isBlank()) {
            errorMessage = "Enter the code from your authenticator app"
            return
        }
        isLoading = true
        errorMessage = null
        scope.launch {
            val result = appState.apiClient.loginWithTwoFactor(token, code.trim())
            isLoading = false
            result.onSuccess { finish(it) }.onFailure {
                errorMessage = describe(it)
                // An expired challenge cannot be retried; go back to the password
                if (it is NibblerApiClient.ApiException && it.message?.contains("expired") == true) backToCredentials(keepError = true)
            }
        }
    }

    fun backToCredentials(keepError: Boolean = false) {
        challengeToken = null
        step = LoginStep.CREDENTIALS
        if (!keepError) errorMessage = null
    }

    private fun finish(auth: AuthResponse) {
        challengeToken = null
        onLoggedIn?.invoke()
        appState.onAuthenticated(auth.user)
        appState.navigateTo(Screen.ProjectsList)
    }

    private fun describe(error: Throwable): String = when (error) {
        is NibblerApiClient.ApiException -> error.message ?: "Login failed"
        else -> "Could not reach the server. Check your connection and the server URL."
    }
}
